import { useContext } from "react";
import { Button, Form, Input, Modal, Table, Tag, Typography } from "antd";
import {
    PlusOutlined,
    QuestionCircleOutlined,
    SearchOutlined,
    TagsOutlined,
} from "@ant-design/icons";

import useSubjectHelpAndSupport from "./useSubjectHelpAndSupport";
import PageHeader from "../../components/PageHeader";
import ActiveFiltersBar from "../../components/ActiveFiltersBar";
import TableCard from "../../components/TableCard";
import TableDicaHint from "../../components/TableDicaHint";
import TablePagination from "../../components/TablePagination";
import { ThemeContext } from "../../contexts/ThemeContext";
import { poeValorComPontosEmMil } from "../../utils/Conversores";

const { Text, Paragraph } = Typography;

export default function SubjectHelpAndSupport() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const {
        isLoading,
        isDeleteModalVisible,
        isLoadingDelete,
        subjectBeingDeleted,
        isCreateModalVisible,
        isLoadingCreate,
        newSubjectName,
        setNewSubjectName,
        editingId,
        columns,
        tableData,
        filteredData,
        searchText,
        setSearchText,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        activeFilterItems,
        handleOpenCreateModal,
        handleCloseCreateModal,
        handleSaveNewSubject,
        handleStartInlineEdit,
        handleConfirmDeleteSubject,
        handleCancelDeleteSubject,
        handleClearAllFilters,
    } = useSubjectHelpAndSupport();

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Page Header */}
            <PageHeader
                icon={<QuestionCircleOutlined />}
                iconBg="linear-gradient(135deg, #13c2c2 0%, #08979c 100%)"
                title="Assuntos de Suporte"
                tag={<Tag color="cyan">{poeValorComPontosEmMil(tableData.length)} cadastrados</Tag>}
                subtitle="Gerencie os assuntos e categorias disponíveis para abertura de chamados e dúvidas pelos cidadãos"
                actions={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleOpenCreateModal}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 600,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Novo Assunto
                    </Button>
                }
            />

            {/* Filter Bar / Controls */}
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                    borderRadius: 10,
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    gap: 14,
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 240, maxWidth: 380 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                        Pesquisar Assunto
                    </span>
                    <Input
                        placeholder="Buscar por nome do assunto..."
                        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setCurrentPage(1);
                        }}
                        allowClear
                        style={{ borderRadius: 8, height: 36 }}
                    />
                </div>
            </div>

            {/* Active Filters Bar */}
            <ActiveFiltersBar
                filters={activeFilterItems}
                onClearAll={handleClearAllFilters}
            />

            {/* Hint & Pagination (Top) */}
            <TableDicaHint text="Clique no ícone de lápis ou dê um duplo clique na linha para alterar o assunto diretamente na tabela. Pressione Enter para salvar ou Esc para cancelar." />

            <TablePagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                itemName="assuntos"
                pageSizeOptions={["10", "25", "50", "100"]}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
            />

            {/* Table Card */}
            <TableCard>
                <Table
                    columns={columns as any}
                    dataSource={filteredData.slice(
                        (currentPage - 1) * pageSize,
                        currentPage * pageSize
                    )}
                    rowKey="key"
                    loading={isLoading}
                    pagination={false}
                    className={`modern-data-table ${isDark ? "modern-data-table-dark" : ""}`}
                    scroll={{ x: 500 }}
                    onRow={(record) => ({
                        onDoubleClick: () => {
                            if (record.id && editingId !== record.id) {
                                handleStartInlineEdit(record);
                            }
                        },
                    })}
                />
            </TableCard>

            {/* Pagination (Bottom) */}
            <TablePagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                itemName="assuntos"
                pageSizeOptions={["10", "25", "50", "100"]}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
            />

            {/* Modal de Criação Rápida */}
            <Modal
                open={isCreateModalVisible}
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <TagsOutlined style={{ color: "#13c2c2", fontSize: 18 }} />
                        <span>Novo Assunto de Suporte</span>
                    </div>
                }
                onCancel={handleCloseCreateModal}
                onOk={handleSaveNewSubject}
                confirmLoading={isLoadingCreate}
                okText="Cadastrar Assunto"
                cancelText="Cancelar"
                okButtonProps={{
                    disabled: !newSubjectName.trim() || isLoadingCreate,
                    loading: isLoadingCreate,
                    style: { fontWeight: 600 },
                }}
                cancelButtonProps={{ disabled: isLoadingCreate }}
                centered
                destroyOnClose
            >
                <div style={{ paddingTop: 10 }}>
                    <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 12.5 }}>
                        Informe o nome do novo assunto. Os cidadãos poderão selecioná-lo ao enviar dúvidas pelo aplicativo.
                    </Text>

                    <Form layout="vertical" onFinish={handleSaveNewSubject}>
                        <Form.Item
                            label={<strong>Nome do Assunto</strong>}
                            required
                        >
                            <Input
                                placeholder="Ex: Dúvidas sobre Sorteios, Problemas com Nota Fiscal, Cadastro..."
                                value={newSubjectName}
                                onChange={(e) => setNewSubjectName(e.target.value)}
                                onPressEnter={(e) => {
                                    e.preventDefault();
                                    handleSaveNewSubject();
                                }}
                                autoFocus
                                size="large"
                                style={{ borderRadius: 8 }}
                                prefix={<TagsOutlined style={{ color: "#13c2c2" }} />}
                            />
                        </Form.Item>
                    </Form>
                </div>
            </Modal>

            {/* Modal de Confirmação de Exclusão */}
            <Modal
                open={isDeleteModalVisible}
                onOk={handleConfirmDeleteSubject}
                onCancel={handleCancelDeleteSubject}
                confirmLoading={isLoadingDelete}
                okText="Sim, Deletar"
                cancelText="Cancelar"
                okType="danger"
                okButtonProps={{ loading: isLoadingDelete }}
                cancelButtonProps={{ disabled: isLoadingDelete }}
                centered
                title="Confirmar Exclusão de Assunto"
            >
                <Paragraph style={{ marginTop: 12 }}>
                    Deseja realmente deletar o assunto{" "}
                    <strong>"{subjectBeingDeleted?.name}"</strong>?
                </Paragraph>
                <div
                    style={{
                        background: isDark ? "#1f1315" : "#fff1f0",
                        border: `1px solid ${isDark ? "#431418" : "#ffa39e"}`,
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 12.5,
                        color: isDark ? "#ffa39e" : "#cf1322",
                    }}
                >
                    Esta ação removerá este assunto. Chamados já cadastrados anteriormente não perderão seus dados históricos.
                </div>
            </Modal>
        </div>
    );
}

